import {
  colors,
  maxBreakpoints,
  minBreakpoints,
} from '@noths/polaris-client-ribbons-design-system';
import {
  fontFace,
  fontSize,
  inset,
  mediaQuery,
  pxToRem,
  responsiveFontSize,
  stack,
  theme,
} from '@noths/polaris-client-styles';
import { css } from 'styled-components';

const { spacing, text, designSystemFontSizes, fonts } = theme;

import freeDeliveryIcon from 'src/assets/svg/freeDelivery.svg';

export const card = css`
  height: 100%;
  position: relative;
`;

export const link = css`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

const productCardSmallFontSize = css`
  ${fontSize(designSystemFontSizes.BODY.SM)}
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      ${fontSize(designSystemFontSizes.BODY.MD)}
    `,
  )};
`;

/**
 * #1: -webkit-mask-image fixes an issue Safari had with using border-radius with overflow: hidden
 *     on a container with an absolutely positioned element inside it.
 *     https://stackoverflow.com/questions/49066011/overflow-hidden-with-border-radius-not-working-on-safari
 */
export const imageWrapper = css`
  position: relative;
  margin: ${stack(spacing.md)};
  border-radius: ${pxToRem(10)};
  overflow: hidden;
  padding-bottom: 100%;
  width: 100%;
  -webkit-mask-image: -webkit-radial-gradient(white, black); /* See comment #1 */

  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      border-radius: ${pxToRem(10)} 0 0 ${pxToRem(10)};
      &[data-is-even='true'] {
        border-radius: 0 ${pxToRem(10)} ${pxToRem(10)} 0;
      }
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      border-radius: ${pxToRem(20)};
    `,
  )};

  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 0;
    position: absolute;
  }
`;

export const productLabel = css`
  position: absolute;
  left: 0;
  top: ${pxToRem(15)};
`;

const FAVOURITE_BUTTON_HEIGHT = 44;

export const favouriteButtonWrapper = css`
  position: absolute;
  top: 0;
  right: ${pxToRem(spacing.xs)};

  margin-top: ${`calc(100% - ${spacing.xs}px - ${FAVOURITE_BUTTON_HEIGHT}px)`};
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      right: ${pxToRem(spacing.md)};
      margin-top: ${`calc(100% - ${spacing.md}px - ${FAVOURITE_BUTTON_HEIGHT}px)`};
    `,
  )};
`;

export const favouriteButtonClassicOverrides = css`
  .button__background {
    border: solid ${pxToRem(1)} ${colors.brandBluePrimary} !important;
  }

  &:hover {
    box-shadow: 0 ${pxToRem(4)} ${pxToRem(6)} 0 rgba(0, 0, 0, 0.1);
    transform: scale(1.045) !important;
    .button__background {
      background-color: white;
    }
  }
  &:active {
    transform: scale(1);

    .button__background {
      width: 100%;
      height: 100%;
    }
  }
`;

export const sponsored = css`
  display: block;
  margin: ${stack(spacing.xs)};
  padding: ${inset(spacing.md, 0)};
  color: ${colors.brandPurplePrimary};
  ${responsiveFontSize(text.xxs)};
  font-family: ${fonts.PRIMARY_REGULAR.family};
  font-weight: ${fonts.PRIMARY_REGULAR.weight};
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      ${responsiveFontSize(text.xs)};
      width: 70%;
    `,
  )};
`;

export const copyWrapper = css`
  padding: ${inset(spacing.md, 0)};
`;

export const noMarginOnLastChild = css`
  & > :last-child {
    margin-bottom: 0;
  }
`;

export const title = css`
  display: block;
  margin: ${stack(spacing.base)};
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  ${productCardSmallFontSize}
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin: ${stack(spacing.xs)};
      width: 70%;
    `,
  )};
`;

export const prices = css`
  position: static;
  margin: ${stack(spacing.sm)};

  ${mediaQuery(
    { max: maxBreakpoints.M },
    /*
      We want the last element in the product card to have no margin at the bottom, which we
      achieve using the :last-child selector. However, because the partner link is hidden on
      medium and smaller screens, :last-child still selects it as it is still the last child
      in the DOM.
      This is problematic because it prevents the margin being removed from the price element
      when it is the last visible element in the card.
      To get round this, we check to see if the price element is second to last child (which
      is the case when there is no Free Delivery or Event label), and if it is, we remove the
      margin at the bottom.

      Please note: If we were to add another breakpoint between medium and large, this could
      potentially break.
    */
    css`
      &:nth-last-child(2) {
        margin-bottom: 0;
      }
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      position: absolute;
      top: 0;
      right: 0;
      margin-top: ${`calc(100% + ${spacing.md}px)`};
      margin-right: ${pxToRem(spacing.md)};
      width: 26%;
      text-align: right;
    `,
  )};
`;

export const styledPrice = css`
  display: inline-block;
  margin: 0;
  ${productCardSmallFontSize}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      display: block;
    `,
  )};
`;

export const previousPrice = css`
  display: inline-block;
  margin: 0 0 0 ${pxToRem(spacing.xs)};
  ${productCardSmallFontSize}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      display: block;
      margin: 0;
    `,
  )};
`;

export const label = css`
  margin: 0;
  padding: 0.5rem;
  background: #e6e4e4;
`;

export const partner = css`
  margin: ${stack(spacing.lg)};
  display: block;
  ${fontFace(fonts.PRIMARY_REGULAR)};
  ${responsiveFontSize(text.xs)};
  ${mediaQuery(
    { max: maxBreakpoints.M },
    css`
      display: none;
    `,
  )};
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      width: 70%;
    `,
  )};
`;

const FREE_DELIVERY__ICON_WIDTH = 16;

export const labelWrapper = css`
  display: block;
  margin: 0 0 ${pxToRem(spacing.sm)};
`;

export const freeDelivery = css`
  display: inline-block;
  padding: ${inset(spacing.xs, spacing.base)};
  border-radius: ${pxToRem(8)};
  background-color: ${colors.neutralLighter};
  color: ${colors.neutralDark};
  ${fontSize(designSystemFontSizes.BODY.XS)}
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};
  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      background-image: url(${freeDeliveryIcon});
      background-position: ${pxToRem(8)} 50%;
      background-repeat: no-repeat;
      padding-left: ${pxToRem(spacing.xs * 2 + FREE_DELIVERY__ICON_WIDTH)};
    `,
  )};
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      ${fontSize(designSystemFontSizes.BODY.SM)}
    `,
  )};
`;

export const arrivesBy = css`
  display: inline-block;
  border-radius: ${pxToRem(8)};
  padding: ${inset(spacing.xs, spacing.base)};
  background-color: ${colors.neutralLighter};
  color: ${colors.functionalGreenPrimary};
  ${fontSize(designSystemFontSizes.BODY.XS)}
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      ${fontSize(designSystemFontSizes.BODY.SM)}
    `,
  )};
`;
